import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, ArrowLeft, Camera, Upload, Sparkles, AlertCircle } from 'lucide-react';
import api from '../../../utils/api';
import { useLanguage } from '../../../contexts/LanguageContext';
import { DISCOUNT_TIERS as CANONICAL_TIERS } from '../../../utils/discountTiers';

const CATEGORIES = [
  'Bakery & Bread',
  'Beverages',
  'Breakfast & Cereal',
  'Dairy & Eggs',
  'Deli & Prepared Foods',
  'Frozen Foods',
  'Fruits',
  'Health & Nutrition',
  'Household Essentials',
  'Meat & Poultry',
  'Miscellaneous',
  'Oils, Sauces & Spices',
  'Pantry Staples',
  'Pasta, Rice & Grains',
  'Personal Care',
  'Pet Supplies',
  'Baby & Kids',
  'Seafood',
  'Snacks & Candy',
  'Vegetables'
];

const DISCOUNT_TIERS = CANONICAL_TIERS.map((t) => ({
  value: String(t.level),
  label: `Tier ${t.level} - ${t.consumer}% off`,
  labelEs: `Nivel ${t.level} - ${t.consumer}% descuento`,
  discount: t.consumer,
}));

export default function ProductEntryForm({ 
  onCancel, 
  initialData, 
  barcodeResult,
  onSuccess 
}) {
  const { t, isSpanish } = useLanguage();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: initialData?.product_name || '',
    category: initialData?.category || '',
    regular_price: initialData?.price || '',
    quantity: 5,
    discount_level: '2',
    expiration_date: initialData?.expiration_date || '',
    weight: initialData?.weight_lb || '',
    barcode: barcodeResult?.raw_code || '',
    plu_code: initialData?.sku || ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [showOcrPanel, setShowOcrPanel] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateDealPrice = () => {
    const regular = parseFloat(formData.regular_price) || 0;
    const tier = DISCOUNT_TIERS.find(t => t.value === formData.discount_level);
    return regular * (1 - tier.discount / 100);
  };

  // OCR Image Upload Handler
  const handleOcrImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setOcrLoading(true);
    setError(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/barcode/enrich-image', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setOcrResult(response.data);
        
        // Auto-fill form with suggested values
        const suggested = response.data.suggested_values || {};
        if (suggested.expiration_date && !formData.expiration_date) {
          handleChange('expiration_date', suggested.expiration_date);
        }
        if (suggested.weight && !formData.weight) {
          handleChange('weight', suggested.weight);
        }
        if (suggested.regular_price && !formData.regular_price) {
          handleChange('regular_price', suggested.regular_price);
        }
      }
    } catch (err) {
      console.error('OCR enrichment error:', err);
      setError(err.response?.data?.detail || 'Failed to extract label data');
    } finally {
      setOcrLoading(false);
    }
  };

  // Apply OCR suggestion to form
  const applyOcrSuggestion = (field, value) => {
    handleChange(field, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category || !formData.regular_price || !formData.quantity) {
      setError('Please fill in all required fields');
      return;
    }

    if (parseInt(formData.quantity) < 5) {
      setError('Minimum quantity is 5 items');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = barcodeResult ? '/barcode/create-rshd' : '/barcode/manual-entry';
      const payload = barcodeResult ? {
        barcode_result: barcodeResult,
        name: formData.name,
        category: formData.category,
        regular_price: parseFloat(formData.regular_price),
        quantity: parseInt(formData.quantity),
        discount_level: parseInt(formData.discount_level),
        expiration_date: formData.expiration_date || null,
        weight: formData.weight ? parseFloat(formData.weight) : null
      } : {
        name: formData.name,
        category: formData.category,
        regular_price: parseFloat(formData.regular_price),
        quantity: parseInt(formData.quantity),
        discount_level: parseInt(formData.discount_level),
        barcode: formData.barcode || null,
        plu_code: formData.plu_code || null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        expiration_date: formData.expiration_date || null
      };

      const response = await api.post(endpoint, payload);

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.();
        }, 2000);
      }
    } catch (err) {
      console.error('Product entry error:', err);
      setError(err.response?.data?.detail || 'Failed to create sizzling hot deal');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('retailer.product_form.deal_posted', 'Deal Posted!')}
            </h2>
            <p className="text-gray-600">
              {t('retailer.product_form.deal_success', 'Your sizzling hot deal has been created successfully.')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // OCR Enrichment Panel Component
  const renderOcrPanel = () => (
    <Card className="mb-4 border-purple-200 bg-purple-50/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-purple-700">
          <Sparkles className="w-4 h-4" />
          {t('retailer.product_form.ocr_enrichment', 'OCR Label Enrichment')}
        </CardTitle>
        <CardDescription className="text-purple-600">
          {isSpanish 
            ? 'Sube una foto de la etiqueta para extraer fecha de vencimiento, peso y precio'
            : 'Upload a label photo to extract expiration date, weight, and price'}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleOcrImageUpload}
          className="hidden"
          data-testid="ocr-file-input"
        />
        
        <div className="flex gap-2 mb-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={ocrLoading}
            className="flex-1"
            data-testid="ocr-upload-btn"
          >
            {ocrLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isSpanish ? 'Procesando...' : 'Processing...'}</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> {t('retailer.product_form.upload_label', 'Upload Label Photo')}</>
            )}
          </Button>
        </div>

        {/* OCR Results */}
        {ocrResult && ocrResult.enrichment?.fields_extracted > 0 && (
          <div className="space-y-2 p-3 bg-white rounded-lg border">
            <p className="text-xs font-medium text-gray-700">
              {t('retailer.product_form.extracted_fields', 'Extracted Fields')}:
            </p>
            {Object.entries(ocrResult.enrichment.fields || {}).map(([fieldName, fieldData]) => (
              <div 
                key={fieldName}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-gray-600 capitalize">
                  {fieldName.replace(/_/g, ' ')}:
                </span>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                    {fieldData.value}
                  </code>
                  {fieldData.needs_review && (
                    <Badge variant="outline" className="text-[10px] text-yellow-600 border-yellow-400">
                      {isSpanish ? 'Revisar' : 'Review'}
                    </Badge>
                  )}
                  <Badge 
                    variant="secondary" 
                    className={`text-[10px] ${fieldData.confidence > 0.8 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}
                  >
                    {Math.round(fieldData.confidence * 100)}%
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Card>
      <CardHeader>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onCancel}
          className="w-fit mb-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back', 'Back')}
        </Button>
        <CardTitle>
          {barcodeResult 
            ? t('retailer.product_form.confirm_details', 'Confirm Product Details')
            : t('retailer.product_form.manual_entry_title', 'Manual Product Entry')}
        </CardTitle>
        <CardDescription>
          {barcodeResult 
            ? t('retailer.product_form.confirm_desc', 'Review and confirm the product information before posting')
            : t('retailer.product_form.manual_desc', 'Enter product details for items without scannable barcodes')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* OCR Enrichment Panel - Show if we might need to extract data */}
        {renderOcrPanel()}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Product Name */}
          <div className="space-y-2">
            <Label htmlFor="name">{t('retailer.product_form.product_name', 'Product Name')} *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('retailer.product_form.product_name_placeholder', 'e.g., Organic Blueberries')}
              required
              data-testid="product-name-input"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">{t('retailer.product_form.category', 'Category')} *</Label>
            <Select 
              value={formData.category} 
              onValueChange={(value) => handleChange('category', value)}
            >
              <SelectTrigger data-testid="category-select">
                <SelectValue placeholder={t('retailer.product_form.select_category', 'Select category')} />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Price & Quantity Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="regular_price">{t('retailer.product_form.regular_price', 'Regular Price ($)')} *</Label>
              <Input
                id="regular_price"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.regular_price}
                onChange={(e) => handleChange('regular_price', e.target.value)}
                placeholder="0.00"
                required
                data-testid="price-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quantity">{t('retailer.product_form.quantity', 'Quantity')} *</Label>
              <Input
                id="quantity"
                type="number"
                min="5"
                value={formData.quantity}
                onChange={(e) => handleChange('quantity', e.target.value)}
                required
                data-testid="quantity-input"
              />
            </div>
          </div>

          {/* Discount Tier */}
          <div className="space-y-2">
            <Label htmlFor="discount_level">{t('retailer.product_form.discount_tier', 'Discount Tier')} *</Label>
            <Select 
              value={formData.discount_level} 
              onValueChange={(value) => handleChange('discount_level', value)}
            >
              <SelectTrigger data-testid="discount-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DISCOUNT_TIERS.map(tier => (
                  <SelectItem key={tier.value} value={tier.value}>
                    {isSpanish ? tier.labelEs : tier.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Deal Price Preview */}
          {formData.regular_price && (
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('retailer.product_form.deal_price', 'Deal Price')}:</span>
                <span className="text-2xl font-bold text-green-600">
                  ${calculateDealPrice().toFixed(2)}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {DISCOUNT_TIERS.find(t => t.value === formData.discount_level)?.discount}% {t('retailer.product_form.off_regular', 'off regular price')}
              </p>
            </div>
          )}

          {/* Optional Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="expiration_date">{t('retailer.product_form.expiration_date', 'Expiration Date')}</Label>
              <Input
                id="expiration_date"
                type="date"
                value={formData.expiration_date}
                onChange={(e) => handleChange('expiration_date', e.target.value)}
                data-testid="expiration-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="weight">{t('retailer.product_form.weight_lb', 'Weight (lb)')}</Label>
              <Input
                id="weight"
                type="number"
                step="0.01"
                value={formData.weight}
                onChange={(e) => handleChange('weight', e.target.value)}
                placeholder={isSpanish ? 'Opcional' : 'Optional'}
                data-testid="weight-input"
              />
            </div>
          </div>

          {/* Barcode Fields (for manual entry) */}
          {!barcodeResult && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="barcode">{t('retailer.product_form.barcode_optional', 'Barcode (optional)')}</Label>
                <Input
                  id="barcode"
                  value={formData.barcode}
                  onChange={(e) => handleChange('barcode', e.target.value)}
                  placeholder="UPC/EAN"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="plu_code">{t('retailer.product_form.plu_optional', 'PLU Code (optional)')}</Label>
                <Input
                  id="plu_code"
                  value={formData.plu_code}
                  onChange={(e) => handleChange('plu_code', e.target.value)}
                  placeholder="ej., 4011"
                />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
            data-testid="submit-rshd"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('retailer.product_form.creating_deal', 'Creating Deal...')}</>
            ) : (
              t('retailer.product_form.post_deal', 'Post Sizzling Hot Deal')
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
